import LegalPageLayout, { LegalSection } from "./LegalPageLayout";

const CONTACT_EMAIL = "ai.insightpulse@gmail.com";

export default function PrivacyPolicy({ onClose }) {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      subtitle="How InsightPulse AI handles your data"
      updatedLabel="Last updated: July 2026"
      onClose={onClose}
    >

      <LegalSection title="1. Overview">
        <p>
          This Privacy Policy explains what information InsightPulse AI ("we", "us", "our")
          collects, how it is used, and the choices you have. By using InsightPulse AI, you
          agree to the practices described here.
        </p>
      </LegalSection>

      <LegalSection title="2. Information We Collect">
        <p>We collect the following categories of information:</p>
        <ul className="list-disc list-inside space-y-1.5 pl-1">
          <li>
            <span className="text-gray-200 font-medium">Account information</span> — your name,
            email address, and profile photo, provided via Google Sign-In when you log in.
          </li>
          <li>
            <span className="text-gray-200 font-medium">Uploaded datasets</span> — the CSV or
            Excel files you upload, along with the dashboards, insights, and conversation
            history generated from them.
          </li>
          <li>
            <span className="text-gray-200 font-medium">Usage data</span> — plan, monthly
            analysis usage counters, and general product interactions needed to operate
            subscription limits.
          </li>
          <li>
            <span className="text-gray-200 font-medium">Feedback you submit</span> — any message
            content you choose to send us through the Feedback form.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="3. How We Use Your Information">
        <ul className="list-disc list-inside space-y-1.5 pl-1">
          <li>To operate core features: parsing your data, generating dashboards, and answering your questions.</li>
          <li>To authenticate you and keep your datasets and conversations tied to your account.</li>
          <li>To enforce plan limits (e.g. monthly analysis counts, file size, and row limits).</li>
          <li>To respond to support requests and feedback you send us.</li>
          <li>To maintain and improve the reliability and security of the product.</li>
        </ul>
      </LegalSection>

      <LegalSection title="4. How Your Data Is Processed">
        <p>
          Your uploaded datasets are stored using Google Firebase. When you ask a question in
          the chat, InsightPulse AI sends only a profile of your dataset's structure (column
          names and types) — never your raw rows — to our AI language provider to determine how
          to analyze your data. The actual calculations are performed locally in your browser
          against your full dataset, and only the resulting text answer is generated with AI
          assistance.
        </p>
      </LegalSection>

      <LegalSection title="5. Data Storage & Security">
        <p>
          Your account data, datasets, and conversation history are stored using Google
          Firebase (Authentication and Firestore). We rely on Firebase's security
          infrastructure and access controls to protect your data, and we do not sell your
          personal information or your dataset content to third parties.
        </p>
      </LegalSection>

      <LegalSection title="6. Data Retention & Deletion">
        <p>
          Your datasets remain in your account until you delete them. Deleting a dataset from
          the sidebar permanently removes it and its associated conversation history. You may
          also request full account data deletion by contacting us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-400 hover:text-blue-300 underline">
            {CONTACT_EMAIL}
          </a>.
        </p>
      </LegalSection>

      <LegalSection title="7. Your Rights">
        <p>
          Depending on where you live, you may have rights to access, correct, export, or
          delete your personal data. To exercise any of these rights, contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-400 hover:text-blue-300 underline">
            {CONTACT_EMAIL}
          </a>{" "}
          and we will respond as soon as reasonably possible.
        </p>
      </LegalSection>

      <LegalSection title="8. Children's Privacy">
        <p>
          InsightPulse AI is not directed at children under 13, and we do not knowingly collect
          personal information from children.
        </p>
      </LegalSection>

      <LegalSection title="9. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be
          reflected by updating the "Last updated" date above.
        </p>
      </LegalSection>

      <LegalSection title="10. Contact Us">
        <p>
          Questions about this Privacy Policy or your data can be sent to{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-400 hover:text-blue-300 underline">
            {CONTACT_EMAIL}
          </a>.
        </p>
      </LegalSection>

    </LegalPageLayout>
  );
}
